import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useTranslation, Trans } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import ImageUploadField from '../components/ImageUploadField';
import VideoUploadField from '../components/VideoUploadField';
import {
  createArtistApplication,
  getMyArtistApplication,
} from '../services/artistApplicationsService';
import usePageMeta from '../hooks/usePageMeta';
import { useLocale } from '../hooks/useLocale';
import { formatDate as fmtDate } from '../utils/format';
import { clarity } from '../lib/clarity';
import './ApplyArtistPage.css';

const ARTIST_IMAGE_BUCKET = 'artist_applications';
const ARTIST_VIDEO_BUCKET = 'artist_applications';

const EMPTY = {
  name: '',
  slug: '',
  nationality: '',
  about: '',
  accentColor: '',
  profileImageUrl: '',
  showcaseImage1Url: '',
  showcaseImage2Url: '',
  showcaseImage3Url: '',
  studioVideoUrl: '',
  studioVideoCaption: '',
  instagramHandle: '',
  creatorName: '',
  creatorAbout: '',
  creatorProfileImageUrl: '',
};

const STUDIO_VIDEO_CAPTION_MAX = 400;
const ABOUT_MAX = 2000;
const HEX_RE = /^#[0-9a-fA-F]{6}$/;

function toSlug(str) {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const Field = ({ label, hint, required, children }) => (
  <div className="apply-field">
    <label className="apply-label">
      {label}
      {required && <span className="apply-required" aria-hidden="true">*</span>}
    </label>
    {children}
    {hint && <span className="apply-hint">{hint}</span>}
  </div>
);

const Section = ({ title, lead, children }) => (
  <section className="apply-section">
    <h2 className="apply-section-title">{title}</h2>
    {lead && <p className="apply-section-lead">{lead}</p>}
    <div className="apply-section-body">{children}</div>
  </section>
);

const ApplyArtistPage = () => {
  const { user, session, loading: authLoading } = useAuth();
  const locale = useLocale();
  const { t } = useTranslation('artist');

  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [existing, setExisting] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [uploading, setUploading] = useState({});
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const isUploading = useMemo(
    () => Object.values(uploading).some(Boolean),
    [uploading]
  );

  // Stable upload setters — one per field so ImageUploadField / VideoUploadField
  // don't see a new onUploadingChange reference on every parent render.
  const uploadSetters = useMemo(() => ({
    profile:         (v) => setUploading((p) => ({ ...p, profile: v })),
    creatorPortrait: (v) => setUploading((p) => ({ ...p, creatorPortrait: v })),
    show1:           (v) => setUploading((p) => ({ ...p, show1: v })),
    show2:           (v) => setUploading((p) => ({ ...p, show2: v })),
    show3:           (v) => setUploading((p) => ({ ...p, show3: v })),
    video:           (v) => setUploading((p) => ({ ...p, video: v })),
  }), []);

  usePageMeta({
    title: t('apply.metaTitle'),
    description: t('apply.metaDescription'),
  });

  const accessToken = session?.access_token;

  useEffect(() => {
    let cancelled = false;
    if (!accessToken) {
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    getMyArtistApplication(accessToken)
      .then((data) => {
        if (!cancelled) setExisting(data?.application || null);
      })
      .catch(() => {
        if (!cancelled) setExisting(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accessToken]);

  if (authLoading) {
    return (
      <div className="apply-shell">
        <div className="apply-loading">{t('apply.loading')}</div>
      </div>
    );
  }
  if (!user) {
    return <Navigate to="/account" replace state={{ next: '/account/apply-artist' }} />;
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'creatorName' && !value.trim()) {
      setForm((prev) => ({ ...prev, creatorName: value, creatorAbout: '', creatorProfileImageUrl: '' }));
      return;
    }
    if (name === 'instagramHandle') {
      setForm((prev) => ({ ...prev, instagramHandle: value.replace(/^@+/, '') }));
      return;
    }
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: toSlug(value),
    }));
  };

  const accentColorInvalid =
    form.accentColor.trim() !== '' && !HEX_RE.test(form.accentColor.trim());

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (accentColorInvalid) return;
    setSaving(true);
    setSaveError(null);
    const payload = {
      name: form.name.trim(),
      slug: form.slug.trim() || undefined,
      nationality: form.nationality.trim() || undefined,
      about: form.about.trim() || undefined,
      accentColor: form.accentColor.trim() || undefined,
      profileImageUrl: form.profileImageUrl.trim() || undefined,
      showcaseImage1Url: form.showcaseImage1Url.trim() || undefined,
      showcaseImage2Url: form.showcaseImage2Url.trim() || undefined,
      showcaseImage3Url: form.showcaseImage3Url.trim() || undefined,
      studioVideoUrl: form.studioVideoUrl.trim() || undefined,
      studioVideoCaption: form.studioVideoCaption.trim() || undefined,
      instagramHandle: form.instagramHandle.trim() || undefined,
      creatorName: form.creatorName.trim() || undefined,
      creatorAbout: form.creatorAbout.trim() || undefined,
      creatorProfileImageUrl: form.creatorProfileImageUrl.trim() || undefined,
    };
    try {
      const data = await createArtistApplication(accessToken, payload);
      setExisting(data.application);
      setSubmitted(true);
      clarity.event('artist_application_submitted');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setSaveError(err.message || t('apply.saveError'));
    } finally {
      setSaving(false);
    }
  };

  // States that don't show the form
  if (loading) {
    return (
      <div className="apply-shell">
        <div className="apply-loading">{t('apply.loadingApplication')}</div>
      </div>
    );
  }

  if (existing && existing.status === 'pending' && !submitted) {
    return (
      <div className="apply-shell">
        <div className="apply-state">
        <h1 className="apply-state-title">{t('apply.states.pendingTitle')}</h1>
        <p>
          <Trans
            i18nKey="artist:apply.states.pendingBody"
            values={{
              date: fmtDate(existing.createdAt, locale, {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              }),
              email: existing.customerEmail,
            }}
            components={{ strong: <strong /> }}
          />
        </p>
        <Link to="/account" className="apply-state-link">{t('apply.states.backToAccount')}</Link>
      </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="apply-shell">
        <div className="apply-state">
        <h1 className="apply-state-title">{t('apply.states.submittedTitle')}</h1>
        <p>
          <Trans
            i18nKey="artist:apply.states.submittedBody"
            values={{ email: user.email }}
            components={{ strong: <strong /> }}
          />
        </p>
        <Link to="/account" className="apply-state-link">{t('apply.states.backToAccount')}</Link>
      </div>
      </div>
    );
  }

  if (existing && existing.status === 'approved') {
    return (
      <div className="apply-shell">
        <div className="apply-state">
        <h1 className="apply-state-title">{t('apply.states.approvedTitle')}</h1>
        <p>
          {existing.slug ? (
            <Trans
              i18nKey="artist:apply.states.approvedBodyWithSlug"
              values={{ slug: existing.slug }}
              components={{
                slugLink: <Link to={`/artist/${existing.slug}`} />,
              }}
            />
          ) : (
            t('apply.states.approvedBodyNoSlug')
          )}
        </p>
        <Link to="/artists" className="apply-state-link">{t('apply.states.viewAllArtists')}</Link>
      </div>
      </div>
    );
  }

  const rejected = existing && existing.status === 'rejected';

  return (
    <div className="apply-shell">
    <div className="apply-page">
      <header className="apply-header">
        <h1 className="apply-title">{t('apply.title')}</h1>
        <p className="apply-intro">
          {t('apply.intro')}
        </p>
      </header>

      {rejected && (
        <div className="apply-alert apply-alert-warning">
          <strong>{t('apply.rejected.title')}</strong>
          {existing.rejectReason && <p style={{ margin: '0.5rem 0 0' }}>{existing.rejectReason}</p>}
          <p style={{ margin: '0.5rem 0 0' }}>
            {t('apply.rejected.tryAgain')}
          </p>
        </div>
      )}

      {saveError && (
        <div className="apply-alert apply-alert-error">{saveError}</div>
      )}

      <form className="apply-form" onSubmit={handleSubmit}>
        <Section
          title={t('apply.sections.basic')}>
          <Field
            label={t('apply.fields.name')}
            hint={form.slug ? t('apply.fields.nameSlugHint', { slug: form.slug }) : undefined}
            required
          >
            <input
              type="text"
              name="name"
              required
              maxLength={200}
              value={form.name}
              onChange={handleNameChange}
              className="apply-input"
              placeholder={t('apply.fields.namePlaceholder')}
            />
          </Field>

          <Field
            label={t('apply.fields.creatorName')}
            hint={form.creatorName.trim()
              ? t('apply.fields.creatorNameHint', { name: form.creatorName.trim() })
              : undefined}
          >
            <input
              type="text"
              name="creatorName"
              maxLength={200}
              value={form.creatorName}
              onChange={handleChange}
              className="apply-input"
              placeholder={t('apply.fields.creatorNamePlaceholder')}
            />
          </Field>

          <ImageUploadField
            label={t('apply.fields.profileImage')}
            value={form.profileImageUrl}
            onChange={(url) => setForm((prev) => ({ ...prev, profileImageUrl: url }))}
            bucket={ARTIST_IMAGE_BUCKET}
            onUploadingChange={uploadSetters.profile}
            hideUrlInput
          />

          <Field
            label={t('apply.fields.nationality')}
            hint={t('apply.fields.nationalityHint')}
          >
            <input
              type="text"
              name="nationality"
              maxLength={100}
              value={form.nationality}
              onChange={handleChange}
              className="apply-input"
              placeholder={t('apply.fields.nationalityPlaceholder')}
            />
          </Field>

          <Field
            label={t('apply.fields.instagram')}
            hint={t('apply.fields.instagramHint')}
          >
            <input
              type="text"
              name="instagramHandle"
              maxLength={100}
              value={form.instagramHandle}
              onChange={handleChange}
              className="apply-input"
              placeholder={t('apply.fields.instagramPlaceholder')}
            />
          </Field>
        </Section>

        <Section
          title={t('apply.sections.identity')}        >
          <Field
            label={t('apply.fields.about')}
            hint={t('apply.fields.charCounter', { current: form.about.length, max: ABOUT_MAX })}
          >
            <textarea
              name="about"
              maxLength={ABOUT_MAX}
              rows={5}
              value={form.about}
              onChange={handleChange}
              className="apply-input apply-textarea"
              placeholder={t('apply.fields.aboutPlaceholder')}
            />
          </Field>

          {form.creatorName.trim() && (
            <>
              <Field
                label={t('apply.fields.creatorAbout')}
                hint={t('apply.fields.creatorAboutHint', { name: form.creatorName.trim() })}
              >
                <textarea
                  name="creatorAbout"
                  maxLength={ABOUT_MAX}
                  rows={5}
                  value={form.creatorAbout}
                  onChange={handleChange}
                  className="apply-input apply-textarea"
                  placeholder={t('apply.fields.creatorAboutPlaceholder')}
                />
              </Field>
              <ImageUploadField
                label={t('apply.fields.creatorProfileImage')}
                value={form.creatorProfileImageUrl}
                onChange={(url) => setForm((prev) => ({ ...prev, creatorProfileImageUrl: url }))}
                bucket={ARTIST_IMAGE_BUCKET}
                onUploadingChange={uploadSetters.creatorPortrait}
                hideUrlInput
              />
            </>
          )}

          <Field
            label={t('apply.fields.accentColor')}
          >
            <div className="apply-color-row">
              <input
                type="color"
                aria-label={t('apply.fields.colorPickerLabel')}
                value={HEX_RE.test(form.accentColor) ? form.accentColor : '#1f2937'}
                onChange={(e) => setForm((prev) => ({ ...prev, accentColor: e.target.value }))}
                className="apply-color-swatch"
              />
              <input
                type="text"
                name="accentColor"
                maxLength={7}
                value={form.accentColor}
                onChange={handleChange}
                className={`apply-input apply-mono${accentColorInvalid ? ' apply-input-error' : ''}`}
                placeholder="#1f2937"
              />
            </div>
            {accentColorInvalid && (
              <span className="apply-hint apply-hint-error">
                {t('apply.fields.accentColorError')}
              </span>
            )}
          </Field>
        </Section>

        <Section
          title={t('apply.sections.showroom')}
        >
          <div className="apply-section-subtitle">{t('apply.sections.featuredProjects')}</div>
          <p className="apply-section-note">
            {t('apply.sections.featuredNote')}
          </p>

          <ImageUploadField
            label={t('apply.fields.showcase1')}
            value={form.showcaseImage1Url}
            onChange={(url) => setForm((prev) => ({ ...prev, showcaseImage1Url: url }))}
            bucket={ARTIST_IMAGE_BUCKET}
            onUploadingChange={uploadSetters.show1}
            hideUrlInput
          />
          <ImageUploadField
            label={t('apply.fields.showcase2')}
            value={form.showcaseImage2Url}
            onChange={(url) => setForm((prev) => ({ ...prev, showcaseImage2Url: url }))}
            bucket={ARTIST_IMAGE_BUCKET}
            onUploadingChange={uploadSetters.show2}
            hideUrlInput
          />
          <ImageUploadField
            label={t('apply.fields.showcase3')}
            value={form.showcaseImage3Url}
            onChange={(url) => setForm((prev) => ({ ...prev, showcaseImage3Url: url }))}
            bucket={ARTIST_IMAGE_BUCKET}
            onUploadingChange={uploadSetters.show3}
            hideUrlInput
          />

          <VideoUploadField
            label={t('apply.fields.studioVideo')}
            value={form.studioVideoUrl}
            onChange={(url) => setForm((prev) => ({ ...prev, studioVideoUrl: url }))}
            bucket={ARTIST_VIDEO_BUCKET}
            onUploadingChange={uploadSetters.video}
            hideUrlInput
            helperText={t('apply.fields.studioVideoHelper')}
          />

          <Field
            label={t('apply.fields.studioVideoCaption')}
            hint={t('apply.fields.studioVideoCaptionHint', { max: STUDIO_VIDEO_CAPTION_MAX, remaining: STUDIO_VIDEO_CAPTION_MAX - form.studioVideoCaption.length })}
          >
            <textarea
              name="studioVideoCaption"
              rows={3}
              value={form.studioVideoCaption}
              onChange={handleChange}
              maxLength={STUDIO_VIDEO_CAPTION_MAX}
              placeholder={t('apply.fields.studioVideoCaptionPlaceholder')}
              className="apply-input apply-textarea"
            />
          </Field>

        </Section>

        <div className="apply-submit-row">
          <label className="apply-terms">
            <input
              type="checkbox"
              required
              checked={acceptedTerms}
              onChange={(e) => setAcceptedTerms(e.target.checked)}
              className="apply-terms-checkbox"
              aria-describedby="apply-terms-text"
            />
            <span id="apply-terms-text" className="apply-terms-text">
              <Trans
                i18nKey="artist:apply.terms"
                components={{
                  termsLink: <Link to="/terminos-y-condiciones" target="_blank" rel="noopener noreferrer" />,
                  strong: <strong />,
                }}
              />{' '}
              <span className="apply-terms-required" aria-hidden="true">*</span>
            </span>
          </label>
          <button
            type="submit"
            disabled={saving || isUploading || !form.name.trim() || !acceptedTerms || accentColorInvalid}
            className="apply-submit"
          >
            {saving
              ? t('apply.submitting')
              : isUploading
              ? t('apply.uploading')
              : t('apply.submit')}
          </button>
        </div>
      </form>
    </div>
    </div>
  );
};

export default ApplyArtistPage;
